/*
 * SPDX-License-Identifier: MIT
 * Copyright (c) 2024 Tech Inverge Solutions Pvt Ltd
 */

import React from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';
import Header from './components/Header';
import Footer from './components/Footer';
import MetaPixelTracker from './components/MetaPixelTracker';
import HomePage from './pages/HomePage';
import ProductListingPage from './pages/ProductListingPage';
import ProductDetailPage from './pages/ProductDetailPage';
import CheckoutPage from './pages/CheckoutPage';
import AdminLogin from './pages/AdminLogin';
import AdminPanel from './pages/AdminPanel';
import LoginPage from './pages/LoginPage';
import ProfilePage from './pages/ProfilePage';
import DeveloperLogin from './pages/DeveloperLogin';
import DomainTracking from './pages/DomainTracking';
import BannerManagement from './pages/BannerManagement';
import NewsletterManagement from './pages/NewsletterManagement';
import OrderDetailPage from './pages/OrderDetailPage';
import OrderManagement from './pages/OrderManagement';
import DashboardOverview from './pages/DashboardOverview';
import UserManagement from './pages/UserManagement';
import AdminLayout from './components/AdminLayout';
import { CartProvider } from './context/CartContext';

import { UserProvider } from './context/UserContext';

const CustomerLayout: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <div className="min-h-screen bg-white flex flex-col">
    <Header />
    <main className="flex-grow">
      {children}
    </main>
    <Footer />
  </div>
);

// CustomerLayout remains same

function App() {
  return (
    <HelmetProvider>
      <UserProvider>
        <CartProvider>
          <Router>
            <MetaPixelTracker />
            <Routes>
              {/* Customer Routes */}
              <Route path="/" element={<CustomerLayout><HomePage /></CustomerLayout>} />
              <Route path="/products" element={<CustomerLayout><ProductListingPage /></CustomerLayout>} />
              <Route path="/products/:id" element={<CustomerLayout><ProductDetailPage /></CustomerLayout>} />
              <Route path="/checkout" element={<CustomerLayout><CheckoutPage /></CustomerLayout>} />
              <Route path="/order/:id" element={<CustomerLayout><OrderDetailPage /></CustomerLayout>} />
              <Route path="/login" element={<CustomerLayout><LoginPage /></CustomerLayout>} />
              <Route path="/reset-password" element={<CustomerLayout><LoginPage /></CustomerLayout>} />
              <Route path="/profile" element={<CustomerLayout><ProfilePage /></CustomerLayout>} />
              
              {/* Admin Routes */}
              <Route path="/admin/login" element={<AdminLayout><AdminLogin /></AdminLayout>} />
              <Route path="/admin" element={<AdminLayout><DashboardOverview /></AdminLayout>} />
              <Route path="/admin/products" element={<AdminLayout><AdminPanel /></AdminLayout>} />
              <Route path="/admin/orders" element={<AdminLayout><OrderManagement /></AdminLayout>} />
              <Route path="/admin/users" element={<AdminLayout><UserManagement /></AdminLayout>} />
              <Route path="/admin/banners" element={<AdminLayout><BannerManagement /></AdminLayout>} />
              <Route path="/admin/newsletter" element={<AdminLayout><NewsletterManagement /></AdminLayout>} />
              
              {/* Developer Routes */}
              <Route path="/developer" element={<AdminLayout><DeveloperLogin /></AdminLayout>} />
              <Route path="/developer/domains" element={<AdminLayout><DomainTracking /></AdminLayout>} />
            </Routes>
          </Router>
        </CartProvider>
      </UserProvider>
    </HelmetProvider>
  );
}

export default App;
